/**
 * ADR 0036 — the nightly call-data purge, explained and brakeable.
 *
 * Two switches govern this feature and only one of them belongs here. Whether
 * an environment may delete at all is deploy-gated
 * (`CALL_RETENTION_PURGE_ENABLED`) because a mistaken enable is unrecoverable
 * and there is never a reason to need it *fast*. Whether deletion is currently
 * running is this card's Pause button, because in an incident you want it
 * stopped in seconds and a mistaken pause costs nothing but disk.
 *
 * The copy deliberately says "395 days", not "a year". The dashboard's window
 * is 365 days but deletion happens 30 days later, and a panel that rounded
 * both to "a year" would be wrong by a month in the direction that generates
 * support questions ("why is this 380-day-old call still in the export?").
 *
 * The shell is `CollapsibleSection` — the same component behind Dialpad Users
 * and the Assessment Runner — so this card can't drift from its neighbours the
 * next time one of them is restyled. Two things follow from collapsing by
 * default, and both are deliberate: the brake sits in the header, because an
 * incident brake behind an extra click is a slower brake; and the subtitle
 * carries the status, because the failure this card exists to catch is a purge
 * that stopped quietly, and that must stay legible without opening anything.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, type RetentionSettings } from "../../lib/api";
import { CollapsibleSection } from "./shared";

type Tone = "on" | "paused" | "off" | "stale";

function StatusDot({ tone }: { tone: Tone }) {
  const color =
    tone === "on"
      ? "bg-emerald-500"
      : tone === "stale"
        ? "bg-red-500"
        : tone === "paused"
          ? "bg-amber-500"
          : "bg-muted-foreground";
  return (
    // `mt-1.5` centres the 8px dot on the first 20px line rather than on the
    // whole block: the row is `items-start`, because the overdue message runs
    // to three lines and a vertically-centred dot lands mid-paragraph, reading
    // as a stray bullet instead of a status light. For the one-line states the
    // result is identical. `shrink-0` keeps a long message from squashing it.
    <span
      aria-hidden="true"
      className={`mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full ${color}`}
    />
  );
}

/** Hours for the first two days, then days — "73 hours" reads as a number to
 * decode, "3 days" reads as a duration. */
function formatElapsed(hours: number): string {
  if (hours < 48) {
    const h = Math.max(0, Math.round(hours));
    return `${h} hour${h === 1 ? "" : "s"}`;
  }
  const d = Math.round(hours / 24);
  return `${d} days`;
}

function formatRunAt(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString("en-NZ", {
        timeZone: "Pacific/Auckland",
        dateStyle: "medium",
        timeStyle: "short",
      });
}

/**
 * The header line, readable with the card shut. Same `tone` as the dot below,
 * so a collapsed card can never claim something the open one contradicts.
 *
 * Deliberately not the body's status sentence: this is a glanceable chip, that
 * is the explanation. Repeating the sentence verbatim would spend the one line
 * a collapsed card gets, and read as a stutter once opened.
 */
function StatusSummary({
  tone,
  data,
}: {
  tone: Tone;
  data: RetentionSettings;
}) {
  if (tone === "off") return <>Not enabled here</>;
  if (tone === "paused")
    return (
      <span className="text-amber-600 dark:text-amber-500">
        Paused by an operator
      </span>
    );
  if (tone === "stale")
    return (
      <span className="text-red-600 dark:text-red-500">
        Overdue — nothing has run
        {data.hours_since_last_run != null &&
          ` in ${formatElapsed(data.hours_since_last_run)}`}
      </span>
    );
  return (
    <>
      Runs nightly at {data.run_at_local} ·{" "}
      {data.last_run
        ? `last cleanup ${formatRunAt(data.last_run.at)}`
        : "no cleanup yet"}
    </>
  );
}

function LastRunLine({ data }: { data: RetentionSettings }) {
  if (!data.last_run) {
    return (
      <div className="text-sm text-muted-foreground">
        Last run — none yet.
        {data.never_run && ` The first is due at ${data.run_at_local}.`}
      </div>
    );
  }
  const { at, calls_deleted, exhausted } = data.last_run;
  return (
    <div className="text-sm text-muted-foreground">
      Last run — {formatRunAt(at)} NZ ·{" "}
      <span className="text-foreground">
        {calls_deleted.toLocaleString()} call
        {calls_deleted === 1 ? "" : "s"} deleted
      </span>
      {!exhausted && (
        // A run that stopped at its batch ceiling looks identical to a clean
        // sweep unless we say so. Surfacing it is the whole point of the flag.
        <span className="text-amber-600 dark:text-amber-500">
          {" "}
          · stopped early, more still to delete
        </span>
      )}
    </div>
  );
}

export function RetentionCard() {
  const qc = useQueryClient();
  const q = useQuery<RetentionSettings>({
    queryKey: ["retention-settings"],
    queryFn: () => api.retentionSettings(),
  });
  const pauseM = useMutation({
    mutationFn: (paused: boolean) => api.setRetentionPaused(paused),
    onSuccess: (data) => qc.setQueryData(["retention-settings"], data),
  });

  const data = q.data;
  // Order matters, and it is the same order the status line below renders in:
  // a deliberately-off or paused purge is not overdue, whatever the flag says.
  // Deriving both from one chain means the dot and the sentence can never
  // contradict each other, even against an API that predates these fields.
  const tone: Tone = !data?.enabled
    ? "off"
    : data.paused
      ? "paused"
      : data.overdue
        ? "stale"
        : "on";

  return (
    <CollapsibleSection
      title="Automatic cleanup"
      subtitle={data ? <StatusSummary tone={tone} data={data} /> : "Loading…"}
      // Open on the two states someone has to act on: a purge that has stopped,
      // and a pause that by design will never lift itself. The control that
      // resolves either one lives inside, so staying shut would hide the remedy.
      // A manual collapse still wins for the rest of the session.
      autoExpand={!!data?.enabled && (data.paused || !!data.overdue)}
      headerActions={
        data?.enabled && (
          <>
            {pauseM.error && (
              <span className="text-xs text-destructive">
                Couldn't save — try again.
              </span>
            )}
            <button
              type="button"
              disabled={pauseM.isPending}
              onClick={() => pauseM.mutate(!data.paused)}
              // Filled while paused, outlined while running. Filled is this
              // codebase's primary-action style, and while deletion is stopped,
              // resuming it is the decision left open.
              className={`rounded px-3 py-1.5 text-sm disabled:opacity-50 transition-colors duration-[var(--duration-base)] ease-[var(--easing-standard)] ${
                data.paused
                  ? "bg-primary text-primary-foreground hover:bg-primary/90"
                  : "border border-input bg-card text-foreground hover:bg-muted/40"
              }`}
            >
              {pauseM.isPending
                ? "Saving…"
                : data.paused
                  ? "Resume automatic cleanup"
                  : "Pause automatic cleanup"}
            </button>
          </>
        )
      }
    >
      <div className="p-4">
        {q.isLoading || !data ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              Every night at{" "}
              <span className="text-foreground">{data.run_at_local}</span>,
              calls older than{" "}
              <span className="text-foreground">{data.purge_days} days</span>{" "}
              are permanently deleted — transcripts, assessment results and
              sentiment along with them. Deleting cannot be undone.
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              The dashboard only ever shows the last {data.display_days} days.
              The extra {data.grace_days} are kept on disk so period-on-period
              comparisons at the edge of that window still have something to
              compare against — which is why this number is {data.purge_days}{" "}
              and not {data.display_days}.
            </p>

            <div className="mt-3 pt-3 border-t border-border flex flex-col gap-1.5">
              <div className="text-sm flex items-start gap-2">
                <StatusDot tone={tone} />
                {!data.enabled ? (
                  <span className="text-muted-foreground">
                    Not enabled in this environment — turning it on is a
                    configuration change, not a switch here.
                  </span>
                ) : data.paused ? (
                  <span className="text-foreground">
                    Paused — nothing is being deleted.
                  </span>
                ) : data.overdue ? (
                  // The failure this exists for is a *silent* one: cleanup that
                  // simply stops happening looks exactly like a quiet night. Say
                  // it out loud, and say it here rather than only in the logs —
                  // the API is a separate container, so this line still appears
                  // when the worker that should have done the work is dead.
                  <span className="text-red-600 dark:text-red-500">
                    Overdue — expected nightly at {data.run_at_local}, but
                    nothing has run
                    {data.hours_since_last_run != null &&
                      ` in ${formatElapsed(data.hours_since_last_run)}`}
                    . The background worker may have stopped.
                  </span>
                ) : (
                  <span className="text-foreground">
                    Running nightly · next cleanup removes calls before{" "}
                    {data.cutoff_date}
                  </span>
                )}
              </div>
              <LastRunLine data={data} />
            </div>

            {data.enabled && data.paused && (
              <p className="text-xs text-muted-foreground mt-3">
                Pausing does not expire on its own. Cleanup stays off until
                someone resumes it here.
              </p>
            )}
          </>
        )}
      </div>
    </CollapsibleSection>
  );
}
